import { useState, useEffect } from "react";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { searchStudents, studentRequestPass, getStudentRequests, getCourses } from "../lib/api";
import {
  Search, Plus, Clock, CheckCircle2, XCircle, Eye, Loader2,
  User, ArrowLeft, FileText, ClipboardList, Phone, Ban
} from "lucide-react";
import WebcamCapture from "../components/WebcamCapture";
const PASS_TYPES = [
  { value: "short_leave", label: "Short Leave (Few Hours)" },
  { value: "day_pass", label: "Day Pass (Full Day)" },
  { value: "emergency_leave", label: "Emergency Leave" },
  { value: "home_visit", label: "Home Visit" },
  { value: "medical_leave", label: "Medical Leave" },
  { value: "other", label: "Other" },
];

const ReceptionDashboard = () => {
  const [activeTab, setActiveTab] = useState("create");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [student, setStudent] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [studentPhoto, setStudentPhoto] = useState(null);

  const [formData, setFormData] = useState({
    pass_type: "",
    reason: "",
    exit_date: "",
    exit_time: "",
    expected_return_date: "",
    expected_return_time: "",
    destination: "",
    emergency_contact: "",
    student_remarks: "",
  });

  useEffect(() => {
    getCourses().then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const getCourseName = (id) => courses.find(c => c.id === id)?.name || id;

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Enter registration number or student name");
      return;
    }
    setSearching(true);
    setStudent(null);
    setShowForm(false);
    try {
      const { data } = await searchStudents(searchQuery);
      setSearchResults(data);
      if (data.length === 0) toast.info("No students found. Check the registration number.");
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStudent = async (s) => {
    setStudent(s);
    setFormData(prev => ({ ...prev, emergency_contact: s.mobile_number || "" }));
    setShowForm(false);
    loadStudentRequests(s.registration_number);
  };

  const loadStudentRequests = async (regNo) => {
    setLoadingRequests(true);
    try {
      const { data } = await getStudentRequests(regNo);
      setRequests(data);
    } catch {
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  const resetForm = () => {
    setFormData({
      pass_type: "", reason: "", exit_date: "", exit_time: "",
      expected_return_date: "", expected_return_time: "",
      destination: "", emergency_contact: student?.mobile_number || "", student_remarks: "",
    });
  };

  const handleSubmitPass = async () => {
    if (!formData.pass_type || !formData.reason || !formData.exit_date || !formData.exit_time || !formData.destination) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!studentPhoto) {
      toast.error("Student photo is mandatory. Please capture a photo using the webcam.");
      return;
    }
    setSubmitting(true);
    try {
      await studentRequestPass({ ...formData, registration_number: student.registration_number, student_photo: studentPhoto });
      toast.success("Gate pass request submitted successfully!");
      setShowForm(false);
      resetForm();
      setStudentPhoto(null);
      loadStudentRequests(student.registration_number);
    } catch (error) {
      toast.error(error.response?.data?.detail ? (typeof error.response.data.detail === "string" ? error.response.data.detail : "Validation error. Please check all fields.") : "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Reception Portal">
      <div className="space-y-6" data-testid="reception-dashboard">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="create" data-testid="create-pass-tab">
              <Plus className="h-4 w-4 mr-2" /> Create Pass
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <ClipboardList className="h-4 w-4 mr-2" /> Pass History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-6">
            {/* Search Section */}
            <Card className="border-slate-200" data-testid="student-search-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="h-5 w-5" /> Find Student
                </CardTitle>
                <CardDescription>Search by registration number or student name</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Enter Registration Number or Name"
                    className="flex-1"
                    data-testid="reception-search-input"
                  />
                  <Button onClick={handleSearch} disabled={searching} data-testid="reception-search-btn">
                    {searching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                    Search
                  </Button>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && !student && (
                  <div className="mt-4 border rounded-lg" data-testid="search-results">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Reg. No</TableHead>
                          <TableHead>Student Name</TableHead>
                          <TableHead>Father Name</TableHead>
                          <TableHead>Course/Year</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.map((s) => (
                          <TableRow key={s.registration_number} className="cursor-pointer hover:bg-slate-50">
                            <TableCell className="font-mono text-sm">{s.registration_number}</TableCell>
                            <TableCell className="font-medium">{s.name}</TableCell>
                            <TableCell>{s.father_name}</TableCell>
                            <TableCell>{getCourseName(s.course_id)} - Y{s.year}</TableCell>
                            <TableCell>{s.mobile_number}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                onClick={() => handleSelectStudent(s)}
                                data-testid={`select-student-${s.registration_number}`}
                              >
                                Select
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Student Profile + Actions */}
            {student && (
              <>
                <Card className="border-emerald-200 bg-emerald-50/30" data-testid="student-profile-card">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                          <User className="h-7 w-7 text-emerald-700" />
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-semibold text-slate-900" data-testid="student-name">{student.name}</h3>
                          <p className="text-sm text-slate-600">Reg: {student.registration_number}</p>
                          <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-slate-600">
                            <span>Father: <strong>{student.father_name}</strong></span>
                            <span>Course: <strong>{getCourseName(student.course_id)} - Year {student.year}</strong></span>
                            <span>Mobile: <strong>{student.mobile_number}</strong></span>
                            {student.parent_phone && <span>Parent: <strong>{student.parent_phone}</strong></span>}
                            <span>
                              <Badge variant="outline" className="capitalize">
                                {student.hostel_status?.replace("_", " ") || "N/A"}
                              </Badge>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setStudent(null); setShowForm(false); setRequests([]); setStudentPhoto(null); }}
                          data-testid="change-student-btn"
                        >
                          <ArrowLeft className="h-4 w-4 mr-1" /> Change
                        </Button>
                        {student.pass_allowed !== false ? (
                          <Button
                            size="sm"
                            onClick={() => { setShowForm(true); resetForm(); setStudentPhoto(null); }}
                            data-testid="new-pass-btn"
                          >
                            <Plus className="h-4 w-4 mr-1" /> New Pass Request
                          </Button>
                        ) : (
                          <Badge variant="destructive" className="flex items-center gap-1 px-3 py-1.5" data-testid="pass-not-allowed-badge">
                            <Ban className="h-3 w-3" /> Pass Not Allowed
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Pass Request Form */}
                {showForm && (
                  <Card className="border-blue-200" data-testid="pass-request-form">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg">Create Gate Pass Request</CardTitle>
                      <CardDescription>Fill details for {student.name}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Pass Type *</Label>
                          <Select value={formData.pass_type} onValueChange={(v) => setFormData({ ...formData, pass_type: v })}>
                            <SelectTrigger data-testid="pass-type-select">
                              <SelectValue placeholder="Select pass type" />
                            </SelectTrigger>
                            <SelectContent>
                              {PASS_TYPES.map(pt => (
                                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Destination *</Label>
                          <Input
                            value={formData.destination}
                            onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                            placeholder="Where is the student going?"
                            data-testid="destination-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Exit Date *</Label>
                          <Input
                            type="date"
                            value={formData.exit_date}
                            onChange={(e) => setFormData({ ...formData, exit_date: e.target.value })}
                            data-testid="exit-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Exit Time *</Label>
                          <Input
                            type="time"
                            value={formData.exit_time}
                            onChange={(e) => setFormData({ ...formData, exit_time: e.target.value })}
                            data-testid="exit-time-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Expected Return Date</Label>
                          <Input
                            type="date"
                            value={formData.expected_return_date}
                            onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
                            data-testid="return-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Expected Return Time</Label>
                          <Input
                            type="time"
                            value={formData.expected_return_time}
                            onChange={(e) => setFormData({ ...formData, expected_return_time: e.target.value })}
                            data-testid="return-time-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Reason *</Label>
                        <Textarea
                          value={formData.reason}
                          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                          placeholder="Reason for gate pass"
                          rows={3}
                          data-testid="reason-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Emergency Contact</Label>
                          <Input
                            value={formData.emergency_contact}
                            onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                            placeholder="Contact number"
                            data-testid="emergency-contact-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Remarks</Label>
                          <Input
                            value={formData.student_remarks}
                            onChange={(e) => setFormData({ ...formData, student_remarks: e.target.value })}
                            placeholder="Any additional remarks"
                            data-testid="remarks-input"
                          />
                        </div>
                      </div>

                      {/* Webcam Capture */}
                      <WebcamCapture
                        onCapture={setStudentPhoto}
                        onClear={() => setStudentPhoto(null)}
                        capturedPhoto={studentPhoto}
                      />

                      <div className="flex gap-3 pt-2">
                        <Button onClick={handleSubmitPass} disabled={submitting} data-testid="submit-pass-btn">
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                          Submit Request
                        </Button>
                        <Button variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-pass-btn">
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Requests for this student */}
                <Card className="border-slate-200" data-testid="student-requests-card">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5" /> Pass History for {student.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingRequests ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                      </div>
                    ) : requests.length === 0 ? (
                      <p className="text-center py-8 text-slate-500">No pass requests found for this student.</p>
                    ) : (
                      <div className="border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead>Request No.</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Exit Date</TableHead>
                              <TableHead>Destination</TableHead>
                              <TableHead>Reason</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {requests.map((r) => (
                              <TableRow key={r.id}>
                                <TableCell className="font-mono text-xs">{r.request_number}</TableCell>
                                <TableCell><PassTypeBadge type={r.pass_type} /></TableCell>
                                <TableCell>{formatDate(r.exit_date)}</TableCell>
                                <TableCell>{r.destination}</TableCell>
                                <TableCell className="max-w-[200px] truncate text-sm">{r.reason}</TableCell>
                                <TableCell><StatusBadge status={r.status} /></TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedPass(r)}
                                    data-testid={`view-pass-${r.id}`}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">All Recent Pass Requests</CardTitle>
                <CardDescription>Search a student to view their pass history</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 mb-6">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Search by Registration Number or Name"
                    className="flex-1 max-w-md"
                    data-testid="history-search-input"
                  />
                  <Button onClick={handleSearch} disabled={searching} data-testid="history-search-btn">
                    {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    Search
                  </Button>
                </div>
                {student && requests.length > 0 && (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Request No.</TableHead>
                          <TableHead>Student</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Exit Date</TableHead>
                          <TableHead>Destination</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {requests.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="font-mono text-xs">{r.request_number}</TableCell>
                            <TableCell className="font-medium">{student.name}</TableCell>
                            <TableCell><PassTypeBadge type={r.pass_type} /></TableCell>
                            <TableCell>{formatDate(r.exit_date)}</TableCell>
                            <TableCell>{r.destination}</TableCell>
                            <TableCell><StatusBadge status={r.status} /></TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => setSelectedPass(r)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {student && requests.length === 0 && !loadingRequests && (
                  <p className="text-center py-8 text-slate-500">No requests found for {student.name}.</p>
                )}
                {!student && (
                  <p className="text-center py-8 text-slate-400">Search for a student to view their pass history.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Pass Detail Dialog */}
        <Dialog open={!!selectedPass} onOpenChange={() => setSelectedPass(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Pass Request Details</DialogTitle>
            </DialogHeader>
            {selectedPass && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Request No.</p>
                    <p className="font-mono font-medium">{selectedPass.request_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <StatusBadge status={selectedPass.status} />
                  </div>
                  <div>
                    <p className="text-slate-500">Pass Type</p>
                    <PassTypeBadge type={selectedPass.pass_type} />
                  </div>
                  <div>
                    <p className="text-slate-500">Destination</p>
                    <p className="font-medium">{selectedPass.destination}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Exit Date</p>
                    <p>{formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</p>
                  </div>
                  {selectedPass.expected_return_date && (
                    <div>
                      <p className="text-slate-500">Expected Return</p>
                      <p>{formatDate(selectedPass.expected_return_date)} {selectedPass.expected_return_time}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-slate-500">Reason</p>
                    <p>{selectedPass.reason}</p>
                  </div>
                </div>

                {selectedPass.approval_history?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Approval History</p>
                    <div className="space-y-2">
                      {selectedPass.approval_history.map((h, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          {h.action === "approve" ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          <span className="font-medium">{h.approver_name}</span>
                          <span className="text-slate-400">({h.stage?.replace(/_/g, " ")})</span>
                          <span className="text-slate-400 text-xs ml-auto">{formatDateTime(h.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ReceptionDashboard;
